import { useEffect, useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useEvent } from '@/lib/api/queries'
import type { Event } from '@/lib/api/types'
import { formatInEventZone } from '@/lib/eventTime'
import { formatTokenNumber, menuUnits } from './money'

/**
 * The event of the `/events/$eventId/...` page a component is rendered in.
 * The menu screens have already loaded it, so this reads the cache.
 */
export function useRouteEvent(): Event | undefined {
  const params = useParams({ strict: false }) as { eventId?: string }
  return useEvent(Number(params.eventId) || 0).data
}

/**
 * A price in menu units, printed the way the menu list's price cell prints
 * one (routes/.../menu.tsx): dollars on a Direct Pay event (menu units × the
 * token value), whole tokens on a token event. Any other event keeps at most
 * two decimals, so a discounted price never prints as a raw float. Same
 * defaults as the menu while the event is loading.
 */
export function formatMenuListPrice(price: number, event: Pick<Event, 'event_type' | 'token_value'> | undefined): string {
  const eventType = event?.event_type || 'token'
  if (eventType === 'direct_pay') {
    // The menu's `token_value || 2.0`.
    const tokenValue = menuUnits(event ?? {}).tokenValue ?? 2
    return `$${(price * tokenValue).toFixed(2)}`
  }
  return formatTokenNumber(price, eventType === 'token')
}

/**
 * When a discount window ends, in the event's timezone: the time alone when
 * it ends today (event-local), with the date otherwise.
 */
export function formatUntil(iso: string, timeZone: string | null | undefined, now: Date = new Date()): string {
  const sameDay = formatInEventZone(iso, timeZone, 'yyyy-MM-dd') === formatInEventZone(now.toISOString(), timeZone, 'yyyy-MM-dd')
  return formatInEventZone(iso, timeZone, sameDay ? 'h:mm a' : 'MMM d, h:mm a')
}

// setTimeout's longest delay (about 24.8 days); a later end re-arms after it.
const MAX_TIMER_MS = 2_147_483_647

/**
 * The current time (ms), read again just after the earliest of `ends` still
 * ahead, so a view drops a discount window once it closes instead of showing
 * it until the next refetch. One re-render per end, no interval.
 */
export function useEndsClock(ends: ReadonlyArray<string | null | undefined>): number {
  const [now, setNow] = useState(() => Date.now())
  const key = ends.filter(Boolean).join(' ')
  useEffect(() => {
    const next = Math.min(...key.split(' ').map((iso) => Date.parse(iso)).filter((at) => at > now))
    if (!Number.isFinite(next)) return
    const timer = setTimeout(() => setNow(Date.now()), Math.min(Math.max(next - Date.now(), 0) + 250, MAX_TIMER_MS))
    return () => clearTimeout(timer)
  }, [key, now])
  return now
}

/** `iso` is set and at or before `now` (ms). */
export function hasPassed(iso: string | null | undefined, now: number): boolean {
  return !!iso && Date.parse(iso) <= now
}

interface PricedItem { price?: number; discount_price?: number | null; active_discount?: unknown }

/** The item's own sale price when it charges (above 0 and below the list price, as the backend's `sale_amount`), else null. */
export function saleOf(item: PricedItem): number | null {
  const sale = item.discount_price
  return item.price != null && sale != null && sale > 0 && sale < item.price ? sale : null
}

/** Whether the list shows a discounted price for the item: a schedule charging, or its own sale. */
export function hasDiscountedPrice(item: PricedItem): boolean {
  return !!item.active_discount || saleOf(item) != null
}

